package com.flowyride.core.domain.account

import kotlinx.coroutines.flow.StateFlow

enum class UserRole { USER, ADMIN }

/** PENDING until an admin approves the request; REVOKED users can no longer sign in. */
enum class AccountStatus { PENDING, APPROVED, REJECTED, REVOKED }

data class User(
    val id: Long,
    val username: String,
    val email: String,
    val role: UserRole,
    val status: AccountStatus,
) {
    val isAdmin: Boolean get() = role == UserRole.ADMIN
}

/** An access request or an account, as an admin sees it. */
data class ManagedUser(
    val user: User,
    /** What the requester wrote to introduce themselves. */
    val requestMessage: String?,
    val createdAtEpochMillis: Long,
)

data class RegistrationRequest(
    val username: String,
    val email: String,
    val password: String,
    val message: String?,
)

sealed interface SessionState {
    /** Still checking whether a previous session is valid. */
    data object Unknown : SessionState
    data object SignedOut : SessionState
    data class SignedIn(val user: User) : SessionState
}

/** Failures of account operations, mapped to messages by the UI. */
sealed class AccountException(message: String) : Exception(message) {
    class InvalidCredentials : AccountException("Invalid credentials")
    class Pending : AccountException("Access request not approved yet")
    class Refused : AccountException("Access refused")
    class UsernameTaken : AccountException("Username already taken")
    class EmailTaken : AccountException("Email already used")
    class Invalid(details: String) : AccountException(details)
    class TooManyAttempts : AccountException("Too many attempts")
    class Unreachable(cause: Throwable?) : AccountException("Server unreachable: ${cause?.message}")
}

/** Validation rules shared by the apps (instant feedback) and the server (authoritative). */
object AccountRules {
    const val USERNAME_MIN = 3
    const val USERNAME_MAX = 20
    const val PASSWORD_MIN = 10
    const val MESSAGE_MAX = 500
    private val USERNAME = Regex("^[A-Za-z0-9_.-]{$USERNAME_MIN,$USERNAME_MAX}$")
    private val EMAIL = Regex("^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$")

    fun isValidUsername(username: String) = USERNAME.matches(username)
    fun isValidEmail(email: String) = email.length <= 254 && EMAIL.matches(email)
    fun isValidPassword(password: String) = password.length >= PASSWORD_MIN

    /** @return the first broken rule, or null when [request] is acceptable. */
    fun validate(request: RegistrationRequest): String? = when {
        !isValidUsername(request.username) -> "Username: $USERNAME_MIN-$USERNAME_MAX letters, digits, . _ -"
        !isValidEmail(request.email) -> "Invalid email"
        !isValidPassword(request.password) -> "Password: at least $PASSWORD_MIN characters"
        (request.message?.length ?: 0) > MESSAGE_MAX -> "Message: at most $MESSAGE_MAX characters"
        else -> null
    }
}

interface AccountRepository {
    val session: StateFlow<SessionState>

    /** Checks the stored session (token or cookie) with the server. */
    suspend fun restoreSession()

    /** @throws AccountException */
    suspend fun register(request: RegistrationRequest)

    /** @param login username or email. @throws AccountException */
    suspend fun signIn(login: String, password: String)

    /** Signs every other device out. @throws AccountException (InvalidCredentials: wrong current password) */
    suspend fun changePassword(currentPassword: String, newPassword: String)

    suspend fun signOut()
}

interface AdminRepository {
    /** @throws AccountException */
    suspend fun users(): List<ManagedUser>
    suspend fun approve(userId: Long)
    suspend fun reject(userId: Long)
    suspend fun revoke(userId: Long)
    suspend fun setRole(userId: Long, role: UserRole)

    /** @return a temporary password to hand over to the user. */
    suspend fun resetPassword(userId: Long): String
}
